import ApiBase from './Api'
import {
    GetUserByIdRequest,
    GetUserListRequest,
    GetUserMeEncryptionKeyRequest,
    GetUserMeRequest,
    PatchUserMeRequest,
    PatchUserRequest,
    PostUserRequest,
    PostUserRequestResetPassword,
    PostUserResetPassword,
    UserListQueryParameters,
} from '@finance-lab/utils/types'

const USER_API_PATHS = {
    REQUEST_PASSWORD_RESET_CODE: '/password-code',
    RESET_PASSWORD_WITH_CODE: '/password-reset',
    USER: (userId: string) => `/users/${userId}`,
    NOTES_ENCRYPTION_KEY: '/users/me/notes-encryption-key',
} as const

const ADMIN_USER_API_PATHS = {
    ALL_USERS: '/admin/users',
    CREATE_USER: '/admin/users',
    GET_USER: (userId: string) => `/admin/users/${userId}`,
    UPDATE_USER: (userId: string) => `/admin/users/${userId}`,
    DELETE_USER: (userId: string) => `/admin/users/${userId}`,
}

export type AdminFindUsersParams = Omit<UserListQueryParameters, 'page' | 'limit'> & {
    paginationModel: { page: number; pageSize: number }
}

class UserApi extends ApiBase {
    async getMe() {
        const user = await this._getRequest<GetUserMeRequest>(USER_API_PATHS.USER('me'))

        return user
    }

    async updateMe(data: PatchUserMeRequest['Body']) {
        const user = await this._patchRequest<PatchUserMeRequest>(USER_API_PATHS.USER('me'), data)

        return user
    }

    async getNotesEncryptionKey() {
        const response = await this._getRequest<GetUserMeEncryptionKeyRequest>(USER_API_PATHS.NOTES_ENCRYPTION_KEY)
        return response.encryptionKey
    }

    async createUser(userData: PostUserRequest['Body']) {
        return this._postRequest<PostUserRequest>(ADMIN_USER_API_PATHS.CREATE_USER, userData)
    }

    async connectToCustomer(userId: string, customerId: string) {
        return this._patchRequest<PatchUserRequest>(ADMIN_USER_API_PATHS.UPDATE_USER(userId), {
            customerId,
        })
    }

    async disconnectFromCustomer(userId: string) {
        return this._patchRequest<PatchUserRequest>(ADMIN_USER_API_PATHS.UPDATE_USER(userId), {
            customerId: null,
        })
    }

    async adminFindUsers({ paginationModel, ...filters }: AdminFindUsersParams) {
        return this._getRequest<GetUserListRequest>(ADMIN_USER_API_PATHS.ALL_USERS, {
            queryParameters: {
                ...filters,
                page: paginationModel.page,
                limit: paginationModel.pageSize,
            },
        })
    }

    async adminGetUser(userId: string) {
        return this._getRequest<GetUserByIdRequest>(ADMIN_USER_API_PATHS.GET_USER(userId))
    }

    async adminFindUsersForCustomer(customerId: string) {
        return this.adminFindUsers({
            customerId,
            paginationModel: { page: 0, pageSize: 100 },
            userCategory: 'all',
            sortField: 'name',
            sortDirection: 'asc',
        })
    }

    async adminDeleteUser(userId: string) {
        return this._deleteRequest(ADMIN_USER_API_PATHS.DELETE_USER(userId))
    }

    async adminSetUserPassword(userId: string, password: string) {
        return this._patchRequest<PatchUserRequest>(ADMIN_USER_API_PATHS.UPDATE_USER(userId), {
            password,
        })
    }

    async adminSetUserCategory(
        userId: string,
        userCategory: PatchUserRequest['Body']['userCategory'],
        trialPeriod?: { startsAt: Date; endsAt: Date },
    ) {
        const body: PatchUserRequest['Body'] = {
            userCategory: userCategory,
        }
        if (trialPeriod) {
            body.trialPeriod = trialPeriod
        }
        return this._patchRequest<PatchUserRequest>(ADMIN_USER_API_PATHS.UPDATE_USER(userId), body)
    }

    async requestPasswordResetCode(email: string) {
        return this._postRequest<PostUserRequestResetPassword>(USER_API_PATHS.REQUEST_PASSWORD_RESET_CODE, {
            email,
        })
    }

    async resetPasswordWithCode(email: string, code: string, newPassword: string) {
        return this._postRequest<PostUserResetPassword>(USER_API_PATHS.RESET_PASSWORD_WITH_CODE, {
            email,
            code,
            password: newPassword,
        })
    }
}

export default UserApi
